/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-extraneous-dependencies */

import { useState, useEffect, useRef, useContext } from "react";
import {
  Badge,
  Button,
  Combobox,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { CircleX, RefreshCcw, SlidersHorizontal } from "lucide-react";
import { FaFileExcel } from "react-icons/fa";
import { Loader as KendoLoader } from "@progress/kendo-react-indicators";
import {
  useLookupActivityTypes,
  useLookupAllAssignmentGroups,
  useLookupServiceAssignmentGroups,
  useLookupCccallsAssignmentGroups,
  useLookupCashCenters,
  useLookupCustomers,
  useLookupAllMachinesServicePoints,
} from "services/lookups";
import { defaultFilters, FiltersContext } from "store/filters-context";

const PERIODS = [
  { id: "All", text: "All" },
  { id: "Today", text: "Today" },
  { id: "Yesterday", text: "Yesterday" },
  { id: "This Week", text: "This Week" },
  { id: "Last Week", text: "Last Week" },
  { id: "This Month", text: "This Month" },
];

const STATUS_LIST = [
  { id: "All", text: "All" },
  { id: "Draft", text: "Draft" },
  { id: "Open", text: "Open" },
  { id: "Assigned", text: "Assigned" },
  { id: "In Progress", text: "In Progress" },
  { id: "On Hold", text: "On Hold" },
  { id: "Resolved", text: "Resolved" },
  // { id: "Awaiting Customer Approval", text: "Awaiting Customer Approval" },
  { id: "Closed", text: "Closed" },
  // { id: "Rejected", text: "Rejected" },
  { id: "Cancelled", text: "Cancelled" },
  { id: "Awaiting Re-Opening", text: "Awaiting Re-Opening" },
];

export default function FilterBarV2({
  hideAsset,
  hideStatus,
  exportExcel,
  onExcelExport,
  exportingExcel,
  onRefresh,
  assetOptions = [],
  isCSMAgent = false,
  pageType = "default",
  incidentActivities = [],
  incidentCategories = [],
  openedForOptions = [],
  regions = [],
}) {
  const { filters, setFilters } = useContext(FiltersContext);

  // --- All possible filter state variables, mapped to their final API keys ---
  // Default/Panic Alert keys
  const [selectedCashCenter, setSelectedCashCenter] = useState(
    filters?.cashCenterId?.value || ""
  );
  const [selectedCustomer, setSelectedCustomer] = useState(
    filters?.customerId?.value || ""
  );
  const [selectedLocation, setSelectedLocation] = useState(
    filters?.assetId?.value || ""
  );
  const [selectedStatus, setSelectedStatus] = useState(
    filters?.status?.value || "All"
  );
  const [selectedDate, setSelectedDate] = useState(
    filters?.period?.value || "All"
  );
  const [selectedAssignment, setSelectedAssignment] = useState(
    filters?.assignmentGroup?.value || ""
  );

  // Default/CSM Accident/Incident keys (using API keys)
  const [selectedActivityType, setSelectedActivityType] = useState(
    filters?.activityType?.value || ""
  );
  const [selectedCsmCategory, setSelectedCsmCategory] = useState(
    filters?.category?.value || ""
  );
  const [selectedCsmOpenedForId, setSelectedCsmOpenedForId] = useState(
    filters?.openedForId?.value || ""
  );
  const [selectedCsmRegion, setSelectedCsmRegion] = useState(
    filters?.region?.value || ""
  );

  const [assignmentGroupList, setAssignmentGroupList] = useState("");
  const [filter, setFilter] = useState(filters?.searchCriteria?.value || "");
  const filterTimeout = useRef(null);

  // lookups
  const { mutate: getCashCenters, data: cashCentersList } =
    useLookupCashCenters();
  const { mutate: getCustomers, data: customersList } = useLookupCustomers();
  const {
    mutate: getLocations,
    data: locationsList,
    reset: resetLocations,
  } = useLookupAllMachinesServicePoints();
  const { mutate: getActivityTypes, data: activityTypesRes } =
    useLookupActivityTypes();

  let assignmentGroupsHook;

  if (pageType === "cccalls") {
    assignmentGroupsHook = useLookupCccallsAssignmentGroups();
  } else if (pageType === "service") {
    assignmentGroupsHook = useLookupServiceAssignmentGroups();
  } else {
    assignmentGroupsHook = useLookupAllAssignmentGroups();
  }

  const {
    mutate: getAssignmentGroups,
    data: assignmentGroupsRes,
    reset: resetAssignmentGroups,
  } = assignmentGroupsHook;

  useEffect(() => {
    // Lookups needed for Panic Alert or Default view
    if (pageType === "panicAlert" || pageType === "default") {
      getCashCenters();
    }
    if (
      pageType === "default" ||
      pageType === "cccalls" ||
      pageType === "service"
    ) {
      getCustomers();
      getActivityTypes();
      getAssignmentGroups();
      if (filters?.customerId?.value) {
        getLocations(filters?.customerId?.value);
      }
    }
  }, [pageType]);

  useEffect(() => {
    // React to external filter context resets/changes
    if (!filters?.cashCenterId && selectedCashCenter) setSelectedCashCenter("");
    if (!filters?.customerId && selectedCustomer) setSelectedCustomer("");
    if (!filters?.assetId && selectedLocation) setSelectedLocation("");
    if (filters?.period?.value === "All" && selectedDate !== "All")
      setSelectedDate("All");
    if (filters?.status?.value === "All" && selectedStatus !== "All")
      setSelectedStatus("All");
    if (!filters?.assignmentGroup && selectedAssignment)
      setSelectedAssignment("");
    if (!filters?.searchCriteria && filter) setFilter("");

    // CSM Accident/Incident filters reset
    if (!filters?.activityType && selectedActivityType)
      setSelectedActivityType("");
    if (!filters?.category && selectedCsmCategory) setSelectedCsmCategory("");
    if (!filters?.openedForId && selectedCsmOpenedForId)
      setSelectedCsmOpenedForId("");
    if (!filters?.region && selectedCsmRegion) setSelectedCsmRegion("");
  }, [filters]);

  useEffect(() => {
    if (assignmentGroupsRes) {
      setAssignmentGroupList(
        (assignmentGroupsRes?.data || [])
          .filter(
            (elm) =>
              !["Call Center", "Administrator", "customerId"].includes(elm.name)
          )
          .map((elm) => ({ label: elm.name, value: elm.name }))
      );
      resetAssignmentGroups();
    }
  }, [assignmentGroupsRes]);

  useEffect(() => {
    if (filter !== null) {
      filterTimeout.current = setTimeout(() => {
        handleAddFilter("searchCriteria", filter, filter, "Search");
        clearTimeout(filterTimeout.current);
        filterTimeout.current = null;
      }, 1000);
    }
    return () => {
      if (filterTimeout.current) {
        clearTimeout(filterTimeout.current);
        filterTimeout.current = null;
      }
    };
  }, [filter]);

  const handleClearFilter = (key) => {
    const updatedFilter = { ...filters };
    if (defaultFilters[key]) {
      updatedFilter[key] = { ...defaultFilters[key] };
    } else {
      delete updatedFilter[key];
    }

    // Reset state variables
    if (key === "cashCenterId") setSelectedCashCenter("");
    else if (key === "customerId") {
      setSelectedCustomer("");
      setSelectedLocation("");
      delete updatedFilter.asset;
    } else if (key === "assetId") setSelectedLocation("");
    else if (key === "activityType")
      setSelectedActivityType(""); // Used by Default & CSM Incident
    else if (key === "category") setSelectedCsmCategory("");
    else if (key === "openedForId") setSelectedCsmOpenedForId("");
    else if (key === "region") setSelectedCsmRegion("");
    else if (key === "assignmentGroup") setSelectedAssignment("");
    else if (key === "status") setSelectedStatus("All");
    else if (key === "period") setSelectedDate("All");
    else if (key === "searchCriteria") setFilter("");

    setFilters({ ...updatedFilter });
  };

  const handleAddFilter = (key, label, value, name) => {
    if (value === "" || value === "All") {
      handleClearFilter(key);
      return;
    }
    const updatedFilter = { ...filters };
    updatedFilter[key] = {
      key,
      value,
      label,
      description: `${name}: ${label}`,
    };

    setFilters({ ...updatedFilter });
  };

  const clearAllFilters = () => {
    setFilters({
      ...defaultFilters,
    });
    // Reset all local state
    setSelectedCashCenter("");
    setSelectedCustomer("");
    setSelectedDate("All");
    setSelectedLocation("");
    setFilter("");
    setSelectedActivityType("");
    setSelectedStatus("All");
    setSelectedAssignment("");
    setSelectedCsmCategory("");
    setSelectedCsmOpenedForId("");
    setSelectedCsmRegion("");
  };

  // --- 1. Renderer for CSM Agent Panic Alert Filters (3 filters) ---
  const renderCsmPanicAlertFilters = () => (
    <>
      {/* Cash Center */}
      <Combobox
        placeholder="Select Cash Center"
        data={[
          { label: "Select Cash Center", value: "" },
          ...((cashCentersList?.data || []).map((elm) => ({
            label: elm.name,
            value: elm.cashCenterId,
          })) || []),
        ]}
        value={selectedCashCenter}
        setValue={(value) => {
          setSelectedCashCenter(value);
          const name =
            cashCentersList?.data.find((elm) => elm.cashCenterId === value)
              ?.name || value;
          handleAddFilter("cashCenterId", name, value, "Cash Center");
        }}
      />

      {/* Asset ID (using assetOptions) */}
      <Combobox
        placeholder="Select Asset"
        data={[{ label: "Select Asset", value: "" }, ...assetOptions]}
        value={selectedLocation}
        setValue={(value) => {
          const name =
            assetOptions.find((a) => a.value === value)?.label || value;
          handleAddFilter("assetId", name, value, "Asset Id");
          setSelectedLocation(value);
        }}
      />

      {/* Status */}
      <Select
        side="bottom"
        onValueChange={(value) => {
          setSelectedStatus(value);
          const statusLabel =
            STATUS_LIST.find((s) => s.id === value)?.text || value;
          handleAddFilter("status", statusLabel, value, "Status");
        }}
        value={selectedStatus}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select Status" />
        </SelectTrigger>
        <SelectContent>
          {STATUS_LIST.map((elm, index) => (
            <SelectItem key={index} value={elm.id}>
              {elm.text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );

  // --- 2. Renderer for CSM Agent Accident/Incident Filters (4 filters) ---
  const renderCsmAccidentIncidentFilters = () => (
    <>
      {/* Activity Type (API Key: activityType) */}
      <Combobox
        placeholder="Select Activity"
        data={[{ label: "Select Activity", value: "" }, ...incidentActivities]}
        value={selectedActivityType}
        setValue={(value) => {
          setSelectedActivityType(value);
          // *** USING CORRECT KEY: activityType ***
          handleAddFilter("activityType", value, value, "Activity");
        }}
      />
      {/* Incident Category (API Key: category) */}
      <Combobox
        placeholder="Select Incident Category"
        data={[
          { label: "Select Incident Category", value: "" },
          ...incidentCategories,
        ]}
        value={selectedCsmCategory}
        setValue={(value) => {
          const label =
            incidentCategories.find((c) => c.value === value)?.label || value;
          setSelectedCsmCategory(value);
          handleAddFilter("category", label, value, "Incident Category");
        }}
      />
      {/* Opened For (API Key: openedForId) */}
      <Combobox
        placeholder="Select Opened For"
        data={[{ label: "Select Opened For", value: "" }, ...openedForOptions]}
        value={selectedCsmOpenedForId}
        setValue={(value) => {
          const label =
            openedForOptions.find((o) => o.value === value)?.label || value;
          setSelectedCsmOpenedForId(value);
          handleAddFilter("openedForId", label, value, "Opened For");
        }}
      />
      <Combobox
        placeholder="Select Region"
        data={[{ label: "Select Region", value: "" }, ...regions]}
        value={selectedCsmRegion}
        setValue={(value) => {
          const label = regions.find((r) => r.value === value)?.label || value;
          setSelectedCsmRegion(value);
          // *** USING CORRECT KEY: region ***
          handleAddFilter("region", label, value, "Region");
        }}
      />
    </>
  );

  const renderDefaultFilters = () => (
    <>
      {/* Cash Center */}
      <Combobox
        placeholder="Select Cash Center"
        data={[
          { label: "Select Cash Center", value: "" },
          ...((cashCentersList?.data || []).map((elm) => ({
            label: elm.name,
            value: elm.cashCenterId,
          })) || []),
        ]}
        value={selectedCashCenter}
        setValue={(value) => {
          setSelectedCashCenter(value);
          const name =
            cashCentersList?.data.find((elm) => elm.cashCenterId === value)
              ?.name || value;
          handleAddFilter("cashCenterId", name, value, "Cash Center");
        }}
      />

      <Combobox
        placeholder="Select Customer"
        data={[
          { label: "Select Customer", value: "" },
          ...((customersList?.data || []).map((elm) => ({
            label: elm.name,
            value: elm.customerId,
          })) || []),
        ]}
        value={selectedCustomer}
        setValue={(value) => {
          setSelectedCustomer(value);
          const name =
            customersList?.data.find((elm) => elm.customerId === value)?.name ||
            value;
          handleAddFilter("customerId", name, value, "Customer");
          setSelectedLocation("");
          if (value) {
            getLocations(value);
          } else {
            resetLocations();
          }
        }}
      />

      {/* Asset (service points) */}
      {hideAsset ? null : (
        <Combobox
          placeholder="Select Asset"
          data={[
            { label: "Select Asset", value: "" },
            ...(assetOptions && assetOptions.length
              ? assetOptions
              : (locationsList?.data || []).map((elm) => ({
                  label: elm.locationId,
                  value: elm.servicePointId,
                })) || []),
          ]}
          value={selectedLocation}
          setValue={(value) => {
            let name = "";
            if (value !== "") {
              const options =
                assetOptions && assetOptions.length
                  ? assetOptions
                  : (locationsList?.data || []).map((elm) => ({
                      label: elm.locationId,
                      value: elm.servicePointId,
                    }));
              const found = options.find((a) => a.value === value);
              name = found ? found.label : value;
            }
            handleAddFilter("assetId", name, value, "Asset Id");
            setSelectedLocation(value);
          }}
        />
      )}

      {/* Activity Type */}
      <Combobox
        placeholder="Select Activity Type"
        data={[
          { label: "Select Activity Type", value: "" },
          ...(activityTypesRes?.data.map((elm) => ({
            label: elm.description,
            value: elm.description,
          })) || []),
        ]}
        value={selectedActivityType}
        setValue={(value) => {
          handleAddFilter("activityType", value, value, "Activity Type");
          setSelectedActivityType(value);
        }}
      />

      {/* Assignment Group */}
      <Combobox
        placeholder="Select Assignment Group"
        data={[
          { label: "Select Assignment Group", value: "" },
          ...assignmentGroupList,
        ]}
        value={selectedAssignment}
        setValue={(value) => {
          handleAddFilter("assignmentGroup", value, value, "Assignment Group");
          setSelectedAssignment(value);
        }}
      />

      {/* Status */}
      {hideStatus ? null : (
        <Select
          side="bottom"
          onValueChange={(value) => {
            setSelectedStatus(value);
            const statusLabel =
              STATUS_LIST.find((s) => s.id === value)?.text || value;
            handleAddFilter("status", statusLabel, value, "Status");
          }}
          value={selectedStatus}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_LIST.map((elm, index) => (
              <SelectItem key={index} value={elm.id}>
                {elm.text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </>
  );

  return (
    <>
      <div className="flex mb-3 gap-1">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="secondary" className="w-max" title="Apply Filters">
              <SlidersHorizontal />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[540px]">
            <h6 className="font-semibold text-lg">Apply Filters</h6>

            <div className="grid grid-cols-2 gap-2 mt-3">
              {pageType === "accidentIncident"
                ? renderCsmAccidentIncidentFilters()
                : pageType === "panicAlert"
                  ? renderCsmPanicAlertFilters()
                  : renderDefaultFilters()}
            </div>

            {/* Buttons: Export (if provided) & Reset */}
            <div className="flex justify-end gap-2 mt-3">
              {/* {exportExcel ? (
                <Button
                  variant="secondary"
                  disabled={exportingExcel}
                  onClick={() => {
                    if (onExcelExport) {
                      onExcelExport();
                    }
                  }}
                >
                  {exportingExcel ? <KendoLoader themeColor="light" /> : null}
                  <FaFileExcel style={{ marginRight: "8px" }} />
                  Export
                </Button>
              ) : null} */}
              <Button
                variant="ghost"
                onClick={() => {
                  // Reset local state variables in the popover
                  setSelectedCashCenter("");
                  setSelectedCustomer("");
                  setSelectedLocation("");
                  setSelectedStatus("All");
                  setSelectedActivityType("");
                  setSelectedAssignment("");
                  setSelectedCsmCategory("");
                  setSelectedCsmOpenedForId("");
                  setSelectedCsmRegion("");
                }}
              >
                Clear
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <div className="min-w-[15%] ">
          <Select
            onValueChange={(e) => {
              setSelectedDate(e);
              handleAddFilter("period", e, e, "Period");
            }}
            value={selectedDate}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((elm, index) => (
                <SelectItem key={index} value={elm.id}>
                  {elm.text}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full">
          <Input
            value={filter}
            placeholder={
              pageType === "default"
                ? "Search: Case No, Status, Activity Type, Category, Sub Category, Bank Reference No, Assignment Group"
                : "Search: Case No, Status, Category, Sub Category"
            }
            title="Search: Case No, Status, Activity Type, Category, Sub Category, Bank Reference Number, Assignment Group"
            onChange={(e) => {
              setFilter(e.target.value);
            }}
          />
        </div>

        {exportExcel ? (
          <Button
            variant="secondary"
            disabled={exportingExcel}
            onClick={() => {
              if (onExcelExport) {
                onExcelExport();
              }
            }}
          >
            {exportingExcel ? <KendoLoader themeColor="light" /> : <></>}
            <FaFileExcel style={{ marginRight: "8px" }} />
            Export
          </Button>
        ) : (
          <></>
        )}
        <Button
          variant="secondary"
          onClick={() => {
            if (onRefresh) onRefresh();
          }}
          title="Refresh List"
        >
          <RefreshCcw style={{ marginRight: "0px", width: "16px" }} />
        </Button>
      </div>
      <div className="flex mb-3 gap-2 flex-wrap items-end">
        {Object.keys(filters).map((elm) => {
          if ((hideStatus && elm === "status") || elm === "period") {
            return <></>;
          }
          return (
            <Badge
              variant="secondary"
              className="gap-3 pr-1 cursor-pointer"
              key={elm}
            >
              {filters[elm].description}
              <CircleX
                onClick={() => {
                  handleClearFilter(elm);
                }}
              />
            </Badge>
          );
        })}
        {Object.keys(filters).length > 2 ? (
          <Badge variant="secondary" className="gap-3 pr-1 cursor-pointer">
            Clear All
            <CircleX
              onClick={() => {
                clearAllFilters();
              }}
            />
          </Badge>
        ) : (
          <></>
        )}
      </div>
    </>
  );
}
